import { useState, useRef, useEffect } from 'react'
import * as am5 from "@amcharts/amcharts5"
import * as am5map from "@amcharts/amcharts5/map"
import serbiaLow from "@amcharts/amcharts5-geodata/serbiaLow"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import { getAmChartsColor } from '../theme/colors'
import { useCustomColors } from '../hooks/useIframe'
// Data will be loaded from API
import { useAppStore, Store } from '../stores/useAppStore'

export default function Map() {
  const chartRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<am5.Root | null>(null)
  const chartRefInstance = useRef<am5map.MapChart | null>(null)
  const polygonSeriesRef = useRef<am5map.MapPolygonSeries | null>(null)
  const pointSeriesForestsRef = useRef<am5map.ClusteredPointSeries | am5map.MapPointSeries | null>(null)
  const pointSeriesSeedRef = useRef<am5map.ClusteredPointSeries | am5map.MapPointSeries | null>(null)
  
  const [showLocationPanel, setShowLocationPanel] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<Store | null>(null)
  const [showMapControls, setShowMapControls] = useState(true)
  const [showForests, setShowForests] = useState(true)
  const [showSeeds, setShowSeeds] = useState(true)
  const [selectedPinType, setSelectedPinType] = useState<'forest' | 'seed' | null>(null)
  const stores = useAppStore((s) => s.storesData)
  const forestCount = useAppStore((s) => s.countForest)
  const seedCount = useAppStore((s) => s.countSeed)
  const loadStores = useAppStore((s) => s.loadStores)
  const [walletAddress] = useState('0x1Ff3a532c992688F2Aee72050c55063f0477a110') // update as needed

  // Get custom colors from iframe context
  const customColors = useCustomColors()

  // Load data from API on mount
  useEffect(() => {
    loadStores(walletAddress)
  }, [loadStores, walletAddress])

  // Initialize amCharts5 map
  useEffect(() => {
    if (!chartRef.current) return

    // Create root element
    const root = am5.Root.new(chartRef.current)
    rootRef.current = root

    // Set theme
    root.setThemes([am5themes_Animated.new(root)])

    // Create map chart
    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "translateX",
        panY: "translateY",
        projection: am5map.geoMercator(),
      })
    )
    chartRefInstance.current = chart

    // Add zoom control
    const zoom = am5map.ZoomControl.new(root, {});
    zoom.plusButton.get('background')?.setAll({
      fill: am5.color(customColors.forest.primary),
    });
    zoom.homeButton.get('background')?.setAll({
      fill: am5.color(customColors.forest.primary),
    })
    chart.set("zoomControl", zoom)

    // Create polygon series for Serbia
    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: serbiaLow,
        calculateAggregates: true,
        valueField: "value",
      })
    )
    polygonSeriesRef.current = polygonSeries

    polygonSeries.mapPolygons.template.setAll({
      fill: am5.color(customColors.background.primary),
      interactive: false,
      strokeWidth: 1,
    })

    // Create point series for forests
    const pointSeriesForests = chart.series.push(
      am5map.ClusteredPointSeries.new(root, {
        affectsBounds: false,
        minDistance: 20,
        scatterDistance: 10,
        scatterRadius: 10,
        stopClusterZoom: 0.9,
        autoScale: true,
      })
    )
    pointSeriesForestsRef.current = pointSeriesForests

    // Setup forest point click handlers
    pointSeriesForests.bullets.push(function () {
      const circle = am5.Circle.new(root, {
        radius: 8,
        tooltipY: 0,
        fill: am5.color(customColors.forest.primary),
        tooltipText: "{municipality} ({location})",
        cursorOverStyle: "pointer",
      })

      circle.events.on("click", function (ev) {
        const dataItem = ev.target.dataItem
        if (dataItem && dataItem.dataContext) {
          setSelectedLocation(dataItem.dataContext as Store)
          setSelectedPinType('forest')
          setShowLocationPanel(true)
        }
      })

      return am5.Bullet.new(root, {
        sprite: circle,
      })
    })

    // Create point series for seeds
    const pointSeriesSeed = chart.series.push(
      am5map.ClusteredPointSeries.new(root, {
        affectsBounds: false,
        minDistance: 20,
        scatterDistance: 10,
        scatterRadius: 10,
        stopClusterZoom: 0.9,
        autoScale: true,
      })
    )
    pointSeriesSeedRef.current = pointSeriesSeed

    // Setup seed point click handlers
    pointSeriesSeed.bullets.push(function () {
      const circle = am5.Circle.new(root, {
        radius: 8,
        tooltipY: 0,
        fill: am5.color(customColors.seed.primary),
        tooltipText: "{municipality} ({location})",
        cursorOverStyle: "pointer",
      })

      circle.events.on("click", function (ev) {
        const dataItem = ev.target.dataItem
        if (dataItem && dataItem.dataContext) {
          setSelectedLocation(dataItem.dataContext as Store)
          setSelectedPinType('seed')
          setShowLocationPanel(true)
        }
      })

      return am5.Bullet.new(root, {
        sprite: circle,
      })
    })

    pointSeriesForests.set("clusteredBullet", function (root) {
      const container = am5.Container.new(root, {
        cursorOverStyle: "pointer",
      });
      // circle1
      container.children.push(
        am5.Circle.new(root, {
          radius: 8,
          tooltipY: 0,
          fill: am5.color(customColors.forest.primary),
        })
      );
      // circle2
      container.children.push(
        am5.Circle.new(root, {
          radius: 10,
          fillOpacity: 0.3,
          tooltipY: 0,
          fill: am5.color(customColors.forest.primary),
        })
      );
      // circle3
      container.children.push(
        am5.Circle.new(root, {
          radius: 12,
          fillOpacity: 0.3,
          tooltipY: 0,
          fill: am5.color(customColors.forest.primary),
        })
      );
      // label
      container.children.push(
        am5.Label.new(root, {
          centerX: am5.p50,
          centerY: am5.p50,
          fill: am5.color(0xffffff),
          populateText: true,
          fontSize: "8",
          text: "{value}",
        })
      );

      container.events.on("click", function (e) {
        if (e.target.dataItem) {
          pointSeriesForests.zoomToCluster(e.target.dataItem);
        }
      });

      return am5.Bullet.new(root, {
        sprite: container,
      });
    });

    pointSeriesSeed.set("clusteredBullet", function (root) {
      const container = am5.Container.new(root, {
        cursorOverStyle: "pointer",
      });
      // circle1
      container.children.push(
        am5.Circle.new(root, {
          radius: 8,
          tooltipY: 0,
          fill: am5.color(customColors.seed.primary),
        })
      );
      // circle2
      container.children.push(
        am5.Circle.new(root, {
          radius: 10,
          fillOpacity: 0.3,
          tooltipY: 0,
          fill: am5.color(customColors.seed.primary),
        })
      );
      // circle3
      container.children.push(
        am5.Circle.new(root, {
          radius: 12,
          fillOpacity: 0.3,
          tooltipY: 0,
          fill: am5.color(customColors.seed.primary),
        })
      );
      // label
      container.children.push(
        am5.Label.new(root, {
          centerX: am5.p50,
          centerY: am5.p50,
          fill: am5.color(0x000000),
          populateText: true,
          fontSize: "8",
          text: "{value}",
        })
      );

      container.events.on("click", function (e) {
        if (e.target.dataItem) {
          pointSeriesSeed.zoomToCluster(e.target.dataItem);
        }
      });

      return am5.Bullet.new(root, {
        sprite: container,
      });
    });

    // Add legend
    const legend = chart.children.push(am5.Legend.new(root, {
      nameField: "name",
      fillField: "color",
      strokeField: "color",
      centerX: am5.percent(0),
      x: am5.percent(0),
      centerY: am5.percent(100),
      y: am5.percent(96),
      marginTop: -20
    }))
    legend.data.setAll([{
      name: "Sadnja",
      color: am5.color(getAmChartsColor('forest', customColors))
    }, {
      name: "Sakupljanje semena", 
      color: am5.color(getAmChartsColor('seed', customColors))
    }])

    // Make chart animate on load
    chart.appear(1000, 100)

    return () => {
      root.dispose()
    }
  }, [])

  // Update map data when stores or toggles change
  useEffect(() => {
    if (!pointSeriesForestsRef.current || !pointSeriesSeedRef.current || !polygonSeriesRef.current) return

    // Clear existing data
    pointSeriesForestsRef.current.data.clear()
    pointSeriesSeedRef.current.data.clear()
    polygonSeriesRef.current.data.clear()

    // Set polygon data
    polygonSeriesRef.current.data.setAll(stores)
    // Count
    // Add forest data
    if (showForests) {
      const forestStores = stores.filter(store => store.type !== "sakupljanje semena")
        forestStores.forEach(store => {
        pointSeriesForestsRef.current!.data.push({
          geometry: { type: "Point", coordinates: [store.lng, store.lat] },
          ...store,
        })
      })
    }

    // Add seed data
    if (showSeeds) {
      const seedStores = stores.filter(store => store.type === "sakupljanje semena")
      seedStores.forEach(store => {
        pointSeriesSeedRef.current!.data.push({
          geometry: { type: "Point", coordinates: [store.lng, store.lat] },
          ...store,
        })
      })
    }
  }, [stores, showForests, showSeeds])

  const closeLocationPanel = () => {
    setShowLocationPanel(false)
    setSelectedLocation(null)
    setSelectedPinType(null)
  }
  const showPopup = () => {
    const popup = document.getElementById("popup");
    popup?.classList.add("h-2/3");
  }

  return (
    <div className="flex flex-col h-full pb-8">
       <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col min-h-0">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-3 justify-center">
              <button 
                onClick={() => setShowForests(!showForests)}
                className={`btn text-sm rounded-full ${
                  showForests 
                    ? 'btn-primary text-white' 
                    : 'btn-secondary'
                }`}
                style={showForests ? { backgroundColor: customColors.background.primary } : {}}
              >
                🌲 Šume {showForests && `(${forestCount})`}
              </button>
              <button 
                onClick={() => setShowSeeds(!showSeeds)}
                className={`btn text-sm rounded-full ${
                  showSeeds 
                    ? 'btn-primary text-gray-800' 
                    : 'btn-secondary'
                }`}
                style={showSeeds ? { backgroundColor: customColors.seed.primary } : {}}
              >
                🌱 Seme {showSeeds && `(${seedCount})`}
              </button>
            </div>
          </div>
          
          <div className="relative flex-1 min-h-0">
            <div
              ref={chartRef}
              className="w-full h-full md:w-[700px] md:mx-auto mb-24"
              style={{ minHeight: '400px' }}
            />
            
            {/* Map overlay with instructions */}
            {showMapControls && (
              <div className="absolute top-4 right-4 bg-white bg-opacity-90 rounded-lg p-3 max-w-xs border border-gray-300 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    Upravljanje mapom.
                  </h3>
                  <button
                    onClick={() => setShowMapControls(false)}
                    className="text-gray-500 hover:text-gray-700 ml-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Klikni na markere na mapi da visiš detalje.</li>
                  <li>• Koristi Šume/Seme tastere iznad.</li>
                  <li>• Zumiraj i pomeraj mapu.</li>
                </ul>
              </div>
            )}

            {/* Map legend */}
            {/*<div className="absolute bottom-4 right-4 bg-white bg-opacity-90 rounded-lg p-3">
              <h4 className="font-semibold text-gray-900 text-sm mb-2">
                Legenda
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                  <span className="text-gray-600">Sadnja</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded mr-2"></div>
                  <span className="text-gray-600">Sakupljanje semena</span>
                </div>
              </div>
            </div> */}
          </div>
        </div>

      {/* Location Selection Panel */}
      {showLocationPanel && (
        <>
          {/* Panel - semi-transparent background */}
        <div id='popup' onClick={showPopup} className="absolute bottom-0 lg:bottom-[8rem] lg:left-[2rem] z-50 flex items-end bg-white bg-opacity-95 backdrop-blur-sm rounded-t-2xl w-full lg:w-1/3 h-1/3 lg:h-2/3 shadow-2xl transition-all duration-300 ease-out">
          <div key={`${selectedPinType}-${selectedLocation?.municipality}-${selectedLocation?.location}`} className="p-6 h-full overflow-y-auto w-full">
            {/* Handle bar with close button */}
            <div className="flex justify-between items-center mb-4">
              <div className="w-0 h-0"></div>
              <div className="w-8 h-2 bg-gray-300 rounded-full"></div>
              <button
                onClick={closeLocationPanel}
                className="text-gray-500 hover:text-gray-700 p-1"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Header */}
            <div className="text-center mb-4 lg:text-left">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedPinType === 'forest' ? '🌲 Šumska površina' : 
                  selectedPinType === 'seed' ? '🌱 Sakupljanje semena' :
                  '📍 Selektovane lokacije'}
              </h3>
              <p className="text-sm text-gray-600">
                {selectedLocation && `${selectedLocation.municipality} - ${selectedLocation.location}`}
              </p>
              <p className="text-xs text-gray-500">
                Koordinate: {selectedLocation && `${selectedLocation.lat.toFixed(4)}, ${selectedLocation.lng.toFixed(4)}`}
              </p>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {selectedPinType === 'forest' && selectedLocation && (
                <>
                  <div className="rounded-lg p-4">
                    <h4 className="font-medium mb-2">🌲 Informacije o šumi</h4>
                    <p className="text-sm">
                      Ovo šumsko područje {selectedLocation.municipality} sadrži zrelo drveće pogodno za održivu seču.
                      Ekosistem podržava raznovrstan divlji svet i obezbeđuje odličnu sekvestraciju ugljenika.
                    </p>
                  </div>
                  <hr className='border-0 h-2 bg-gray-300 rounded-full'/>
                  <div className="rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Detalji lokacije</h4>
                    <p className="text-sm text-gray-700">
                      <strong>Grad:</strong> {selectedLocation.municipality}<br/>
                      <strong>Adresa:</strong> {selectedLocation.location}<br/>
                      <strong>Tip:</strong> {selectedLocation.type}<br/>
                      <strong>Vrste:</strong> <span className='bg-green-900 text-green-50 rounded-full p-1'>{selectedLocation.trees}</span>
                    </p>
                  </div>
                  <hr className='border-0 h-2 bg-gray-300 rounded-full'/>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Zauzeta površina</div>
                    <div className='font-bold text-base text-green-50'>{selectedLocation.size} m²</div>
                  </div>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Zasađeno drveća</div>
                    <div className='font-bold text-base text-green-50'>{selectedLocation.amount}</div>
                  </div>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Godina</div>
                    <div className='font-bold text-base text-green-50'>{selectedLocation.year}</div>
                  </div>
                </>
              )}

              {selectedPinType === 'seed' && selectedLocation && (
                <>
                  <div className="rounded-lg p-4">
                    <h4 className="font-medium mb-2">🌱 Sakupljanje semena</h4>
                    <p className="text-sm">
                      Ova lokacija u {selectedLocation.municipality} je idealna za sakupljanje semena.
                      Područje je identifikovano kao izvor visokokvalitetnog semena sa odličnom genetskom raznolikošću.
                    </p>
                  </div>
                  <hr className='border-0 h-2 bg-gray-300 rounded-full'/>
                  <div className="rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Detalji lokacije</h4>
                    <p className="text-sm text-gray-700">
                      <strong>Grad:</strong> {selectedLocation.municipality}<br/>
                      <strong>Adresa:</strong> {selectedLocation.location}<br/>
                      <strong>Tip:</strong> {selectedLocation.type}<br/>
                      <strong>Vrste:</strong> <span className='bg-green-900 text-green-50 rounded-full p-1'>{selectedLocation.trees}</span>
                    </p>
                  </div>
                  <hr className='border-0 h-2 bg-gray-300 rounded-full'/>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Zauzeta površina</div>
                    <div className='detail-value font-bold text-base text-green-50'>{selectedLocation.size} m²</div>
                  </div>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Zasađeno drveća</div>
                    <div className='detail-value font-bold text-base text-green-50'>{selectedLocation.amount}</div>
                  </div>
                  <div className="rounded flex justify-between items-baseline p-2 max-w-screen-md" style={{ backgroundColor: customColors.background.primary }}>
                    <div className='text-sm text-white'>Godina</div>
                    <div className='detail-value font-bold text-base text-green-50'>{selectedLocation.year}</div>
                  </div>
                </>
              )}

              {!selectedPinType && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Detalji lokacije</h4>
                  <p className="text-sm text-gray-700">
                    Klikni na pin da vidiš specifične informacije o šuma ili seme lokacijama.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
      )}
    </div>
  )
}
